import React, { useState } from 'react';
import { Calculator, Loader2, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { C, PanelShell } from './shared';

const Field = ({ label, children, hint }) => (
  <label className="text-[10px] flex flex-col gap-0.5" style={{ color: C.slate }}>
    {label}{children}{hint && <span className="text-[9px]" style={{ color: C.green }}>{hint}</span>}
  </label>
);

export const RebuildEstimatePanel = ({ api, pid, profile, onEstimate }) => {
  const prefilledFloor = profile?.floor_area_m2?.confidence && profile.floor_area_m2.confidence !== 'not_available' ? profile.floor_area_m2.value : null;
  const [floor, setFloor] = useState('');
  const [storeys, setStoreys] = useState('');
  const [quality, setQuality] = useState('');
  const [si, setSi] = useState('');
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const [err, setErr] = useState(null);

  const run = async () => {
    setBusy(true); setErr(null);
    const body = {};
    if (floor) body.floor_area_m2 = Number(floor);
    if (storeys) body.storeys = Number(storeys);
    if (quality) body.quality = quality;
    if (si) body.broker_sum_insured = Number(si);
    try {
      const { data } = await api.post(`/property/${encodeURIComponent(pid)}/rebuild-estimate`, body);
      setRes(data); onEstimate && onEstimate(data);
    } catch (e) { console.error('API error:', e?.message); setErr(e?.response?.data?.detail || e?.message); }
    setBusy(false);
  };

  return (
    <PanelShell title="Rebuild estimate" icon={<Calculator className="w-3 h-3" />} testid="property-rebuild-panel"
      meta={<span className="font-semibold uppercase" style={{ color: C.amber }} data-testid="property-rebuild-watermark">Indicative — not Cordell</span>}>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2">
        <Field label="Floor area m²" hint={prefilledFloor ? `prefilled ${prefilledFloor} m² from the footprint — leave blank to use it` : null}>
          <Input value={floor} onChange={e => setFloor(e.target.value)} placeholder={prefilledFloor ? String(prefilledFloor) : 'type (1 input)'} className="rounded-sm h-8 text-xs" data-testid="rebuild-floor-input" />
        </Field>
        <Field label="Storeys">
          <Input value={storeys} onChange={e => setStoreys(e.target.value)} placeholder="1" className="rounded-sm h-8 text-xs" data-testid="rebuild-storeys-input" />
        </Field>
        <Field label="Quality">
          <select value={quality} onChange={e => setQuality(e.target.value)} className="h-8 rounded-sm border border-input bg-white px-2 text-xs" data-testid="rebuild-quality-select">
            <option value="">standard (default)</option>
            <option value="standard">standard</option>
            <option value="quality">quality</option>
            <option value="prestige">prestige</option>
          </select>
        </Field>
        <Field label="Broker sum insured $">
          <Input value={si} onChange={e => setSi(e.target.value)} placeholder="optional" className="rounded-sm h-8 text-xs" data-testid="rebuild-si-input" />
        </Field>
      </div>
      <div className="mt-2 flex items-center gap-3 flex-wrap">
        <Button onClick={run} disabled={busy || !pid} className="rounded-sm h-8" style={{ background: C.blue }} data-testid="rebuild-estimate-btn">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Calculator className="w-3.5 h-3.5" />}<span className="ml-1.5 text-xs">Estimate</span>
        </Button>
        {res && <span className="text-[10px] font-semibold px-2 py-1 rounded" style={{ background: C.bg, color: C.navy }} data-testid="rebuild-typed-counter">typed inputs: {res.typed_inputs}{res.typed_fields?.length ? ` (${res.typed_fields.join(', ')})` : ''} · prefilled: {res.prefilled_fields?.join(', ') || 'none'}</span>}
      </div>
      {err && <p className="mt-2 text-[11px]" style={{ color: C.red }} data-testid="rebuild-error">{String(err)}</p>}
      {res?.error && <p className="mt-2 text-[11px] flex items-center gap-1" style={{ color: C.amber }} data-testid="rebuild-needs-input"><AlertTriangle className="w-3 h-3" /> {res.error}</p>}
      {res?.estimate && (
        <div className="mt-3 relative rounded-sm border border-dashed border-[rgba(178,117,22,0.5)] p-3 overflow-hidden" data-testid="rebuild-result">
          <span className="absolute right-2 top-1 text-[28px] font-black uppercase tracking-widest select-none pointer-events-none" style={{ color: 'rgba(178,117,22,0.08)' }}>Indicative</span>
          <p className="text-[10px] uppercase tracking-wider" style={{ color: C.slate }}>Indicative rebuild — {res.state} · table {res.table_version}</p>
          <p className="text-2xl font-semibold mt-0.5" style={{ color: C.navy }} data-testid="rebuild-estimate-value">${res.estimate.toLocaleString()}</p>
          <p className="text-[11px]" style={{ color: C.slate }}>range ${res.low.toLocaleString()} – ${res.high.toLocaleString()} · {res.formula}</p>
          {res.underinsurance_flag !== null && res.underinsurance_flag !== undefined && (
            <p className="mt-1 text-[11px] font-semibold" style={{ color: res.underinsurance_flag ? C.red : C.green }} data-testid="rebuild-underinsurance">
              {res.underinsurance_flag ? `UNDERINSURANCE FLAG — sum insured ${res.underinsurance.shortfall_pct}% below the estimate (threshold ${res.underinsurance.threshold_pct}%)`
                : `sum insured within ${res.underinsurance.threshold_pct}% of the estimate (${res.underinsurance.shortfall_pct}% below)`}
            </p>
          )}
          <p className="mt-1 text-[9px]" style={{ color: C.slate }}>{res.note}</p>
        </div>
      )}
    </PanelShell>
  );
};
